<template>
    <div class="brand-line-chart-wrapper">
        <div v-if="chartLoading" class="brand-line-chart-loader" role="status" aria-live="polite" aria-label="Loading chart history">
            <v-progress-circular indeterminate size="26" width="3" color="primary" />
            <span class="brand-line-chart-loader__text">Loading history…</span>
        </div>
        <line-chart v-else :dataChart="chartData" :countGraph2="renderChart" :options="graphOptions" />
    </div>
</template>

<script>
    import LineChart from "../charts/LineChart"
    import { resolveBrandColor } from "../../utils/brandColor"

    // Modul-Level Cache (überlebt Remount der Komponente, z.B. beim Wechseln zwischen Dashboard/Prompts-Tabs)
    const BRAND_VISIBILITY_CACHE = new Map()
    const BRAND_VISIBILITY_CACHE_TTL_MS = 5 * 60 * 1000
    const BRAND_VISIBILITY_CACHE_MAX_ENTRIES = 30

    function stableStringify(value) {
        if (value === null || typeof value !== "object") return JSON.stringify(value)
        if (Array.isArray(value)) return "[" + value.map(stableStringify).join(",") + "]"
        const keys = Object.keys(value).sort()
        return "{" + keys.map((k) => JSON.stringify(k) + ":" + stableStringify(value[k])).join(",") + "}"
    }

    function getCachedBrandVisibility(key) {
        const entry = BRAND_VISIBILITY_CACHE.get(key)
        if (!entry) return null
        if (Date.now() - entry.ts > BRAND_VISIBILITY_CACHE_TTL_MS) {
            BRAND_VISIBILITY_CACHE.delete(key)
            return null
        }
        return entry.data
    }

    function setCachedBrandVisibility(key, data) {
        if (BRAND_VISIBILITY_CACHE.size >= BRAND_VISIBILITY_CACHE_MAX_ENTRIES) {
            const oldestKey = BRAND_VISIBILITY_CACHE.keys().next().value
            if (oldestKey) BRAND_VISIBILITY_CACHE.delete(oldestKey)
        }
        BRAND_VISIBILITY_CACHE.set(key, { ts: Date.now(), data })
    }

    export default {
        name: "BrandLineChart",
        components: { LineChart },
        props: {
            selectedDates: Array,
            availableDates: {
                type: Array,
                default: () => []
            },
            dateRangeTouched: { type: Boolean, default: false },
            selectedBrands: Array,
            category_id: String,
            mode: String,
            googleDate: String,
            platform: String,
            platformsCount: Number,
            tags: Array,
            brandColors: { type: Object, default: () => ({}) },
            tagsOperator: { type: String, default: "or" }, // 'or' | 'and'
            // 0 = plot every brand (dashboard default); a report passes a small number so the
            // chart shows only the primary brand + top competitors and stays readable
            maxBrands: { type: Number, default: 0 },
            // canonical name of the project's own brand — always kept when maxBrands trims
            ownBrand: { type: String, default: "" },
            // Öffentliche Report-Freigabe: Firma und Projekt kommen aus dem Report, nicht aus dem Login bzw.
            // der Route (dort ist :id die Report-ID). Leer = wie bisher localStorage / $route.params.id.
            companyId: { type: String, default: "" },
            projectId: { type: String, default: "" }
        },
        data() {
            return {
                renderChart: 0,
                chartData: {
                    labels: [],
                    datasets: []
                },
                pendingHistoryFetch: null,
                historyFetchInFlight: false,
                historyFetchQueued: false
            }
        },
        computed: {
            chartLoading() {
                return !!this.pendingHistoryFetch || this.historyFetchInFlight || this.historyFetchQueued
            },
            graphOptions() {
                // vue-chartjs 3.5.0 nutzt Chart.js 2.x => Legend / Tooltips NICHT unter plugins
                const isRelevance = this.mode === "Relevance"
                const isSentiment = this.mode === "Sentiment"
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    spanGaps: true,
                    legend: {
                        display: false // Legend komplett ausblenden (damit keine Dataset-Toggles sichtbar sind)
                    },
                    tooltips: {
                        mode: "index",
                        intersect: false,
                        callbacks: {
                            title: (items, data) => (items && items[0] ? items[0].label : ""),
                            label: (tooltipItem, data) => {
                                const ds = data.datasets[tooltipItem.datasetIndex] || {}
                                const lbl = ds.label ? ds.label + ": " : ""
                                return lbl + tooltipItem.yLabel
                            }
                        }
                    },
                    scales: {
                        yAxes: [
                            {
                                scaleLabel: {
                                    display: true,
                                    labelString: isSentiment ? "Sentiment Avg (-100 to +100)" : isRelevance ? "Relevance" : "Visibility (%)"
                                },
                                ticks: {
                                    beginAtZero: !isSentiment,
                                    min: isSentiment ? -100 : undefined,
                                    max: isSentiment ? 100 : undefined,
                                    callback: (v) => {
                                        const numeric = Number(v)
                                        if (!Number.isFinite(numeric)) return v
                                        return isSentiment ? numeric.toFixed(0) : numeric.toLocaleString()
                                    }
                                }
                            }
                        ],
                        xAxes: [
                            {
                                gridLines: {
                                    display: false,
                                    drawBorder: false
                                },
                                scaleLabel: {
                                    display: true,
                                    labelString: "Dates"
                                }
                            }
                        ]
                    }
                }
            }
        },
        watch: {
            selectedBrands: {
                handler() {
                    this.requestBrandVisibilityUpdate()
                },
                immediate: true,
                deep: true
            },
            // tags: {
            //     handler() {
            //         this.requestBrandVisibilityUpdate()
            //     },
            //     immediate: true,
            //     deep: true
            // },
            mode: {
                handler() {
                    this.requestBrandVisibilityUpdate()
                },
                immediate: true,
                deep: true
            },
            platform: {
                handler() {
                    this.requestBrandVisibilityUpdate()
                },
                immediate: true,
                deep: true
            },
            availableDates: {
                handler() {
                    this.requestBrandVisibilityUpdate()
                },
                immediate: false,
                deep: true
            },
            selectedDates: {
                handler() {
                    this.requestBrandVisibilityUpdate()
                },
                immediate: true,
                deep: true
            },
            brandColors: {
                handler() {
                    // Nur neu einfärben, kein Refetch nötig
                    this.formatChartData(this._lastBrands || [])
                },
                deep: true
            }
        },
        methods: {
            requestBrandVisibilityUpdate() {
                if (this._destroyed) return
                if (!this.$route?.params?.id) return
                if (!this.selectedBrands || !this.selectedBrands.length) return
                if (this.historyFetchInFlight) {
                    this.historyFetchQueued = true
                    return
                }
                if (this.pendingHistoryFetch) return
                this.pendingHistoryFetch = setTimeout(() => {
                    this.pendingHistoryFetch = null
                    this.runQueuedBrandVisibilityFetch()
                }, 0)
            },
            async runQueuedBrandVisibilityFetch() {
                if (this.historyFetchInFlight) {
                    this.historyFetchQueued = true
                    return
                }
                this.historyFetchInFlight = true
                try {
                    await this.fetchBrandVisibility()
                } finally {
                    this.historyFetchInFlight = false
                    if (this.historyFetchQueued) {
                        this.historyFetchQueued = false
                        this.requestBrandVisibilityUpdate()
                    }
                }
            },
            async fetchBrandVisibility(dates = null) {
                if (this._destroyed) return
                if (!this.selectedBrands || !this.selectedBrands.length) return
                const projectId = this.projectId || this.$route?.params?.id
                if (!projectId) return
                const companyId = this.companyId || localStorage.company_id
                try {
                    const brands = this.selectedBrands.map((b) => {
                        return { canonicalName: b.canonicalName, mentions: b.mentions }
                    })
                    const url =
                        (JSON.parse(process.env.VUE_APP_MONITORING_PROXY_COMPANY_IDS).indexOf(
                            this.CryptoJS.AES.decrypt(companyId, process.env.VUE_APP_ENCRYPTION_PW).toString(this.CryptoJS.enc.Utf8)
                        ) == -1
                            ? process.env.VUE_APP_MONITORING_API_URL
                            : process.env.VUE_APP_APIURL + "/monitoring-api") + "/ai-suite/brandvisibility/history"

                    const post_data = {
                        company_id: companyId,
                        project_id: projectId,
                        brands,
                        googleDate: this.googleDate || null,
                        platform: this.platform || null,
                        category_id: this.category_id || null
                    }
                    const dateFilters = this.getRequestDates()
                    if (dateFilters.length) {
                        post_data.dates = dateFilters
                    }
                    if (this.tags && this.tags.length) {
                        post_data.tags = this.tags
                        post_data.tagsOperator = this.tagsOperator
                    }

                    const cacheKey = stableStringify({ url, post_data })
                    const cached = getCachedBrandVisibility(cacheKey)
                    if (cached) {
                        this.formatChartData(cached.brands)
                        return
                    }

                    const response = await this.$helpers.axiosPost(url, post_data)
                    setCachedBrandVisibility(cacheKey, response.data)
                    this.formatChartData(response.data.brands)
                } catch (e) {
                    this.chartData = { labels: [], datasets: [] }
                }
            },
            formatChartData(brands) {
                this._lastBrands = brands
                if (!brands || !brands.length) {
                    this.chartData = { labels: [], datasets: [] }
                    return
                }
                const isRelevance = this.mode === "Relevance"
                const isSentiment = this.mode === "Sentiment"

                // Alle vorhandenen Tage sammeln
                const allDates = Array.from(new Set(brands.flatMap((b) => (b.visibility || []).map((v) => ((v.date || "").includes("T") ? v.date.split("T")[0] : v.date))))).sort()

                const filteredAllDates = this.filterDatesByAvailability(allDates)
                const sentimentCutoff = "2026-01-15"
                const visibleDates = isSentiment ? filteredAllDates.filter((isoDate) => isoDate >= sentimentCutoff) : filteredAllDates
                this.chartData.labels = visibleDates.map((d) => this.getDayMonthAndYear(d))

                // Brand-Daten normalisieren (Map Tag -> Eintrag)
                const prepared = brands.map((b) => {
                    const dayMap = {}
                    ;(b.visibility || []).forEach((v) => {
                        const day = (v.date || "").includes("T") ? v.date.split("T")[0] : v.date
                        if (!day) return
                        // Falls Backend mehrere Einträge pro Tag liefern würde: additiv für value/salience, Percent überschreiben (ist final)
                        if (!dayMap[day]) {
                            dayMap[day] = {
                                value: 0,
                                salience: 0,
                                percent: null,
                                sentimentSum: 0,
                                sentimentWeight: 0,
                                sentimentAvg: null
                            }
                        }
                        const mentionCountRaw = Number(v.responsesWithMention ?? v.value ?? 0)
                        const mentionCount = Number.isFinite(mentionCountRaw) ? mentionCountRaw : 0
                        const valueIncrement = Number(v.value) || 0
                        const salienceIncrement = typeof v.salienceSum === "number" ? v.salienceSum : typeof v.relevance === "number" ? v.relevance : 0
                        dayMap[day].value += valueIncrement
                        dayMap[day].salience += salienceIncrement
                        if (v.visibilityPercent !== null && v.visibilityPercent !== undefined) {
                            dayMap[day].percent = v.visibilityPercent
                        }
                        const hasSentimentSum = typeof v.sentimentSum === "number"
                        const hasSentimentAvg = typeof v.sentimentAvg === "number"
                        if (hasSentimentSum) {
                            dayMap[day].sentimentSum += v.sentimentSum
                        } else if (hasSentimentAvg && mentionCount > 0) {
                            dayMap[day].sentimentSum += v.sentimentAvg * mentionCount
                        }
                        if (mentionCount > 0) {
                            dayMap[day].sentimentWeight += mentionCount
                        }
                        if (dayMap[day].sentimentWeight > 0) {
                            dayMap[day].sentimentAvg = dayMap[day].sentimentSum / dayMap[day].sentimentWeight
                        } else if (hasSentimentAvg && dayMap[day].sentimentAvg == null) {
                            dayMap[day].sentimentAvg = v.sentimentAvg
                        }
                    })
                    const totalValue = Object.values(dayMap).reduce((a, x) => a + x.value, 0)
                    const totalSalience = Object.values(dayMap).reduce((a, x) => a + x.salience, 0)
                    const sentimentValues = Object.values(dayMap)
                        .map((entry) => (typeof entry.sentimentAvg === "number" ? entry.sentimentAvg : null))
                        .filter((val) => val !== null)
                    const avgSentiment = sentimentValues.length ? sentimentValues.reduce((acc, val) => acc + val, 0) / sentimentValues.length : null
                    return { ...b, _dayMap: dayMap, _totalValue: totalValue, _totalSalience: totalSalience, _avgSentiment: avgSentiment }
                })

                // Sortierung
                let sorted = [...prepared]
                if (isRelevance) {
                    sorted.sort((a, b) => b._totalSalience - a._totalSalience)
                } else if (isSentiment) {
                    sorted.sort((a, b) => {
                        const aVal = typeof a._avgSentiment === "number" ? a._avgSentiment : -Infinity
                        const bVal = typeof b._avgSentiment === "number" ? b._avgSentiment : -Infinity
                        return bVal - aVal
                    })
                } else {
                    sorted.sort((a, b) => b._totalValue - a._totalValue)
                }

                // keep only the top N brands (reports), but never drop the project's own brand
                if (this.maxBrands > 0 && sorted.length > this.maxBrands) {
                    const top = sorted.slice(0, this.maxBrands)
                    const ownName = (this.ownBrand || "").toLowerCase()
                    if (ownName && !top.some((b) => (b.canonicalName || "").toLowerCase() === ownName)) {
                        const own = sorted.find((b) => (b.canonicalName || "").toLowerCase() === ownName)
                        if (own) top.splice(top.length - 1, 1, own)
                    }
                    sorted = top
                }

                // Datasets
                const activeDates = isSentiment ? visibleDates : filteredAllDates

                // Die History-API gruppiert Serien u. U. unter einem anderen kanonischen
                // Namen (z. B. "Mercedes"), als der Nutzer ihn in "Manage Brands" pflegt
                // (z. B. "Mercedes-Benz"). Dadurch stimmen Label und Farb-Key nicht überein.
                // Deshalb jede zurückgelieferte Serie wieder auf die ausgewählte Brand mappen.
                const selectedBrandLookup = this.buildSelectedBrandLookup()

                this.chartData.datasets = sorted.map((brand) => {
                    const label = this.resolveSelectedBrandName(brand, selectedBrandLookup)
                    const color = this.getColorForBrand(label)

                    const data = isRelevance
                        ? activeDates.map((d) => {
                              const entry = brand._dayMap[d]
                              return entry ? entry.salience : 0
                          })
                        : isSentiment
                        ? activeDates.map((d) => {
                              const entry = brand._dayMap[d]
                              if (!entry || typeof entry.sentimentAvg !== "number") return null
                              return Number((entry.sentimentAvg * 100).toFixed(1))
                          })
                        : activeDates.map((d) => {
                              const entry = brand._dayMap[d]
                              if (!entry) return 0
                              if (entry.percent != null) return Number(entry.percent.toFixed(2))
                              return entry.value // Fallback
                          })

                    return {
                        label,
                        data,
                        borderColor: color,
                        backgroundColor: "transparent",
                        pointBackgroundColor: color,
                        pointStyle: "circle",
                        pointRadius: 4,
                        pointHoverRadius: 10,
                        borderWidth: 2
                    }
                })

                this.renderChart++
                // expose brand name + line color so a static legend can be rendered outside
                // the chart (the built-in Chart.js legend is disabled to hide dataset toggles)
                this.$emit(
                    "legend",
                    this.chartData.datasets.map((d) => ({ label: d.label, color: d.borderColor }))
                )
            },
            getRequestDates() {
                const selected = Array.isArray(this.selectedDates) ? this.selectedDates.filter(Boolean) : []
                // Auch beim initialen Laden (dateRangeTouched === false) nur den ausgewählten
                // Bereich abrufen – selectedDates ist per Default bereits das 30-Tage-Fenster.
                if (selected.length >= 2) {
                    return [selected[0], selected[selected.length - 1]]
                }
                if (selected.length === 1) {
                    return [selected[0]]
                }
                const available = Array.isArray(this.availableDates) ? this.availableDates.filter(Boolean) : []
                if (available.length >= 2) {
                    return [available[0], available[available.length - 1]]
                }
                return available.length ? [available[0]] : []
            },
            filterDatesByAvailability(dates = []) {
                const allowed = Array.isArray(this.availableDates) ? this.availableDates.filter(Boolean) : []
                if (!allowed.length) return dates
                const allowedSet = new Set(allowed.map((d) => (d && d.includes("T") ? d.split("T")[0] : d)))
                return dates.filter((day) => allowedSet.has(day))
            },
            getDayMonthAndYear(dateString) {
                // Erwartet YYYY-MM-DD oder vollständigen ISO-String
                const date = new Date(dateString)
                const day = date.getDate()
                const month = date.toLocaleString("default", { month: "long" })
                const year = date.getFullYear()
                return `${day} ${month} ${year}`
            },
            normalizeDate(dateString) {
                // Nur Tagesanteil behalten, ohne Zeitzonen-Verschiebung
                return dateString.split("T")[0]
            },
            getColorForBrand(name) {
                return resolveBrandColor(name, this.brandColors)
            },
            normalizeMatchKey(value) {
                return String(value || "")
                    .trim()
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[̀-ͯ]/g, "")
                    .replace(/[’´`ʻʼ]/g, "'")
                    .replace(/'/g, "")
                    .replace(/\s+/g, " ")
            },
            // Baut eine Map (normalisierter Name/Alias -> canonicalName der ausgewählten Brand),
            // damit von der API zurückgelieferte Serien wieder der vom Nutzer gepflegten Brand
            // (Name + Farbe aus "Manage Brands") zugeordnet werden können.
            buildSelectedBrandLookup() {
                const map = new Map()
                const list = Array.isArray(this.selectedBrands) ? this.selectedBrands : []
                list.forEach((b) => {
                    const canonical = b && b.canonicalName ? b.canonicalName : ""
                    if (!canonical) return
                    const keys = new Set()
                    keys.add(this.normalizeMatchKey(canonical))
                    ;(Array.isArray(b.mentions) ? b.mentions : []).forEach((m) => {
                        const k = this.normalizeMatchKey(m)
                        if (k) keys.add(k)
                    })
                    keys.forEach((k) => {
                        if (k && !map.has(k)) map.set(k, canonical)
                    })
                })
                return map
            },
            resolveSelectedBrandName(brand, lookup) {
                const map = lookup || this.buildSelectedBrandLookup()
                const candidates = []
                if (brand && brand.canonicalName) candidates.push(brand.canonicalName)
                if (brand && Array.isArray(brand.mentions)) candidates.push(...brand.mentions)
                for (const candidate of candidates) {
                    const hit = map.get(this.normalizeMatchKey(candidate))
                    if (hit) return hit
                }
                return brand && brand.canonicalName ? brand.canonicalName : ""
            }
        },
        mounted() {
            this.requestBrandVisibilityUpdate()
        },
        beforeDestroy() {
            this._destroyed = true
            if (this.pendingHistoryFetch) {
                clearTimeout(this.pendingHistoryFetch)
                this.pendingHistoryFetch = null
            }
            this.historyFetchQueued = false
        }
    }
</script>
<style>
    .brand-line-chart-wrapper {
        position: relative;
    }
    .brand-line-chart-wrapper > div {
        position: relative;
        height: 375px; /* <- gewünschte Höhe anpassen */
        width: 100%;
    }

    .brand-line-chart-loader {
        position: absolute;
        inset: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 10px;
        background: rgba(255, 255, 255, 0.72);
        backdrop-filter: blur(1px);
        z-index: 2;
        pointer-events: none;
    }

    .brand-line-chart-loader__text {
        font-size: 12px;
        font-weight: 500;
        color: rgba(0, 0, 0, 0.72);
    }

    .theme--dark .brand-line-chart-loader {
        background: rgba(18, 18, 18, 0.58);
    }

    .theme--dark .brand-line-chart-loader__text {
        color: rgba(255, 255, 255, 0.84);
    }

    /* Falls Chart trotzdem schrumpft: Canvas auf volle Höhe zwingen */
    .brand-line-chart-wrapper canvas {
        height: 100% !important;
    }
</style>
